import React, { Fragment, useMemo } from 'react';

import { ColumnResizeHandle, useResizableColumns } from '../ResizableColumns';
import { cx, defineRecipe, useRecipe } from '../../variants';
import { applyFilters } from '../../filtering';
import type { FilterField, FilterValues } from '../DataTable/DataTable.types';

/**
 * The cell checkbox is a 3 × 2 table — three states crossed with locked — and
 * the hand-written version was a ternary nested three deep that no longer said
 * what it meant. Declared, the awkward corner is explicit: a LOCKED cell that
 * holds the permission is neither "on" nor "off", it is muted-on, and that is
 * a `compoundVariants` row rather than a special case buried in a conditional.
 *
 * Named `matrixBox`, so an app can add a state or reskin one through
 * `UIVariantsProvider` without this file changing.
 */
export const matrixBoxRecipe = defineRecipe('matrixBox', 'ui-matrix__box', {
    variants: {
        state: {
            off: 'ui-matrix__box--off',
            on: 'ui-matrix__box--on',
            partial: 'ui-matrix__box--partial',
        },
        // `yes`/`no`, not CVA's boolean keys — see the note on `chipRecipe`.
        locked: { yes: 'ui-matrix__box--locked', no: '' },
        // EAA-1804: a fourth cell state the design sheet calls out separately —
        // "edited, not yet saved" (amber/hổ phách). It is ORTHOGONAL to on/off/
        // partial: a pending cell can carry any of those three values, it is
        // just the one the user just touched. CVA concatenates every matched
        // axis's class together, so declaring it here is enough for the amber
        // hook to show up next to whichever `state` class is present — no
        // compound row is needed for the general case.
        pending: { yes: 'ui-matrix__box--pending', no: '' },
    },
    compoundVariants: [
        // Locked-and-set: present, but not yours to change. Rendering it as
        // plain "on" invites a click that will not land; rendering it as "off"
        // would misreport the value outright.
        { state: 'on', locked: 'yes', class: 'ui-matrix__box--locked-on' },
        { state: 'partial', locked: 'yes', class: 'ui-matrix__box--locked-on' },
        // EAA-1804: locked cells have no `onToggle`, so a locked-AND-pending
        // cell should not occur in practice — but give it its own hook rather
        // than silently reusing `--pending` if a caller ever marks one pending
        // anyway (e.g. a save that failed mid-flight on a column that was
        // locked out from under it), so an app can special-case or ignore it.
        { pending: 'yes', locked: 'yes', class: 'ui-matrix__box--pending-locked' },
    ],
    defaultVariants: { state: 'off', locked: 'no', pending: 'no' },
});

/** A value column. Its cells are checkboxes. */
export interface MatrixColumn {
    /** Stable identity. Also the key stored widths are saved under. */
    key: string;
    /** Header content — a name, optionally with a lock or badge beside it. */
    header: React.ReactNode;
    /** Second header line: "dựng sẵn · 40", "3 lượt gán · 12". */
    meta?: React.ReactNode;
    /**
     * Cells render but do not accept a click. Use for a column the caller is
     * allowed to READ but not change — a locked column with real ticks in it is
     * information; hiding it is not.
     */
    locked?: boolean;
    width: number;
    minWidth?: number;
    maxWidth?: number;
    /** Exclude from resizing entirely. */
    fixedWidth?: boolean;
}

export interface MatrixRow {
    key: string;
    /** The row-header cell's content — the sticky left column. */
    header: React.ReactNode;
    /**
     * The row's filterable fields, addressed by `FilterField.key` and by
     * `searchKeys`. Plain data, not display nodes — `header` is what the cell
     * shows, this is what the filter reads.
     */
    fields?: Record<string, unknown>;
}

export interface MatrixSection {
    key: string;
    /** Band label, e.g. "TỔ CHỨC". */
    label: React.ReactNode;
    /** Right of the label, e.g. "4 quyền". */
    meta?: React.ReactNode;
    rows: MatrixRow[];
}

/** Every hook the caller needs to skin this without the component shipping CSS. */
export interface MatrixTableClassNames {
    root?: string;
    scroll?: string;
    table?: string;
    /** The `<th>` above the row-header column. */
    cornerHeader?: string;
    columnHeader?: string;
    /** Applied on top of `columnHeader` for leading sticky columns. */
    stickyColumn?: string;
    rowHeader?: string;
    cell?: string;
    sectionRow?: string;
    sectionHeader?: string;
    /** The checkbox itself, plus its three state modifiers. */
    box?: string;
    boxOn?: string;
    boxPartial?: string;
    boxLocked?: string;
    /** EAA-1804: edited-but-not-saved-yet — the design sheet's amber cell. */
    boxPending?: string;
    resizeHandle?: string;
    empty?: string;
}

export interface MatrixTableProps {
    columns: MatrixColumn[];
    sections: MatrixSection[];
    /** Content of the top-left corner cell (usually a sort control + caption). */
    cornerHeader?: React.ReactNode;
    /** Width of the sticky row-header column. */
    rowHeaderWidth: number;
    rowHeaderKey?: string;
    /**
     * How many LEADING value columns stay put on horizontal scroll, on top of
     * the always-sticky row-header column. A tick means nothing once you cannot
     * see which row and column it belongs to, so a wide matrix wants at least 1.
     */
    stickyColumns?: number;
    /** Is this cell ticked? */
    isChecked: (rowKey: string, columnKey: string) => boolean;
    /**
     * EAA-1804: has this cell been changed locally but not yet persisted? Optional
     * and additive — omit it and every cell resolves `pending: false`, so an
     * existing caller renders exactly as before. Section/group-toggle cells never
     * ask this; "pending" describes one row's own edit, not a rollup of many.
     */
    isPending?: (rowKey: string, columnKey: string) => boolean;
    /** Omit to render a read-only matrix. */
    onToggle?: (rowKey: string, columnKey: string, next: boolean) => void;
    /**
     * Omit to hide the per-section toggle row cells. When present, the section
     * row shows on/off/partial for the rows beneath it and clicking sets them
     * all.
     */
    onToggleSection?: (sectionKey: string, columnKey: string, next: boolean) => void;
    /**
     * Enables resizing and remembers widths under `ui:colwidths:<storageKey>`.
     * Omit for a table whose widths should not persist.
     */
    storageKey?: string;
    /** Bump when the column set changes shape enough to invalidate stored widths. */
    storageVersion?: number;
    /** Accessible name for the table. */
    label?: string;
    /* --- filtering: the SAME contract `DataTable` uses ------------------- */

    /**
     * Filter definitions, identical in shape and meaning to `DataTable`'s.
     * Applied by the shared engine (`matchesFilters`) against each row's
     * `fields`, so "filter by X" behaves the same on both surfaces.
     *
     * Filtering here is CLIENT-SIDE: a matrix is a whole grid, and paging or
     * server-filtering its rows would leave column totals and group toggles
     * describing rows that are not on screen. `DataTable` defaults to
     * server-side because its lists are paged; both read the same values.
     */
    filterFields?: FilterField[];
    /** Current values, keyed by `FilterField.key`. */
    filterValues?: FilterValues;
    /** Free-text term, matched against `searchKeys`. */
    search?: string;
    /** Which of a row's `fields` the free-text term looks at. */
    searchKeys?: string[];

    /** Shown in place of the body when `sections` is empty. */
    emptyMessage?: React.ReactNode;
    /** Localised template for a resize grip's accessible name. */
    resizeLabel?: (columnName: string) => string;
    classNames?: MatrixTableClassNames;
}

/**
 * Tier-1 primitive: a grouped row × column matrix of checkboxes with sticky
 * leading columns and drag-resizable widths.
 *
 * STRUCTURE AND BEHAVIOUR ONLY — the skin comes from `classNames`. That split is
 * deliberate: this package ships no CSS, and the console's tables are drawn from
 * a design handoff with exact values that belong beside the rest of that
 * handoff's CSS. What the component owns is the part every such table gets wrong
 * by hand: sticky-offset arithmetic that stays correct as columns resize, the
 * three-state group cell, and the resize/persistence wiring.
 *
 * The sticky offsets are COMPUTED from the live widths rather than written into
 * CSS. A hand-authored `left: 440px` on the second column is correct exactly
 * until someone drags the first one, and then it silently overlaps.
 */
export function MatrixTable({
    columns,
    sections: allSections,
    cornerHeader,
    rowHeaderWidth,
    rowHeaderKey = '__rowheader__',
    stickyColumns = 0,
    isChecked,
    isPending,
    onToggle,
    onToggleSection,
    storageKey,
    storageVersion,
    label,
    filterFields,
    filterValues,
    search,
    searchKeys,
    emptyMessage,
    resizeLabel = (name) => `Resize column ${name}`,
    classNames = {},
}: MatrixTableProps): React.ReactElement {
    /**
     * One filter pass, by the shared engine. A section whose rows all drop goes
     * with them — an empty band is a heading for nothing.
     */
    const sections = useMemo(() => {
        const fields = filterFields ?? [];
        if (fields.length === 0 && !search?.trim()) return allSections;
        const values = filterValues ?? {};
        return allSections
            .map((section) => ({
                ...section,
                rows: applyFilters(section.rows, fields, values, {
                    accessor: (row, key) => row.fields?.[key],
                    search,
                    searchKeys,
                }),
            }))
            .filter((section) => section.rows.length > 0);
    }, [allSections, filterFields, filterValues, search, searchKeys]);
    const resizeCols = useMemo(
        () => [
            { key: rowHeaderKey, width: rowHeaderWidth, min: 200, max: 900 },
            ...columns.map((c) => ({
                key: c.key,
                width: c.width,
                min: c.minWidth ?? 96,
                max: c.maxWidth ?? 480,
                fixed: c.fixedWidth,
            })),
        ],
        [columns, rowHeaderKey, rowHeaderWidth]
    );

    const { widths, startResize, nudge, reset, resizingKey } = useResizableColumns({
        // A table with no storage key still resizes; it just forgets on reload.
        storageKey: storageKey ?? '__ephemeral__',
        columns: resizeCols,
        version: storageVersion,
        storage: storageKey ? undefined : null,
    });

    /**
     * Left offset per sticky column, accumulated from the LIVE widths. This is
     * the whole reason the widths live in a hook rather than in CSS.
     */
    const stickyLeft = useMemo(() => {
        const out = new Map<string, number>();
        let acc = widths[rowHeaderKey] ?? rowHeaderWidth;
        out.set(rowHeaderKey, 0);
        for (let i = 0; i < Math.min(stickyColumns, columns.length); i += 1) {
            out.set(columns[i].key, acc);
            acc += widths[columns[i].key] ?? columns[i].width;
        }
        return out;
    }, [widths, columns, stickyColumns, rowHeaderKey, rowHeaderWidth]);

    /**
     * Sticky cells need an explicit stacking order. Without one a normal cell
     * later in DOM order paints OVER the frozen column as it scrolls under it —
     * the column stays in place but the content slides across it. The header row
     * sits one level above the body for the same reason on the vertical axis.
     */
    function cellStyle(key: string, header = false): React.CSSProperties | undefined {
        const left = stickyLeft.get(key);
        if (left === undefined) return header ? { zIndex: 1 } : undefined;
        return { position: 'sticky', left, zIndex: header ? 3 : 2 };
    }

    const cx = (...parts: (string | undefined | false)[]) =>
        parts.filter(Boolean).join(' ') || undefined;

    const colCount = columns.length + 1;

    return (
        <div className={classNames.root}>
            <div className={classNames.scroll}>
                <table
                    className={classNames.table}
                    aria-label={label}
                    /* `fixed` is not cosmetic here. Under auto layout the browser
                       may widen a column past its <col> width to fit content,
                       and the sticky offsets — computed from the declared widths
                       — then disagree with what is on screen, leaving a gap
                       beside the frozen column. `max-content` keeps the table as
                       wide as its columns so it scrolls rather than squeezing;
                       `minWidth: 100%` stops a narrow table from ending early. */
                    style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}
                >
                    <colgroup>
                        <col style={{ width: widths[rowHeaderKey] ?? rowHeaderWidth }} />
                        {columns.map((c) => (
                            <col key={c.key} style={{ width: widths[c.key] ?? c.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                className={cx(classNames.cornerHeader, classNames.stickyColumn)}
                                style={{ ...cellStyle(rowHeaderKey, true), position: 'sticky', left: 0, zIndex: 3 }}
                            >
                                {cornerHeader}
                                <ColumnResizeHandle
                                    columnKey={rowHeaderKey}
                                    startResize={startResize}
                                    nudge={nudge}
                                    active={resizingKey === rowHeaderKey}
                                    label={resizeLabel(String(label ?? ''))}
                                    onReset={reset}
                                    className={classNames.resizeHandle}
                                />
                            </th>
                            {columns.map((c, i) => (
                                <th
                                    key={c.key}
                                    scope="col"
                                    className={cx(
                                        classNames.columnHeader,
                                        i < stickyColumns && classNames.stickyColumn
                                    )}
                                    style={cellStyle(c.key, true)}
                                >
                                    <span>{c.header}</span>
                                    {c.meta !== undefined && <span>{c.meta}</span>}
                                    {!c.fixedWidth && (
                                        <ColumnResizeHandle
                                            columnKey={c.key}
                                            startResize={startResize}
                                            nudge={nudge}
                                            active={resizingKey === c.key}
                                            label={resizeLabel(textOf(c.header))}
                                            onReset={reset}
                                            className={classNames.resizeHandle}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map((section) => (
                            <Fragment key={section.key}>
                                <tr className={classNames.sectionRow}>
                                    <th
                                        scope="rowgroup"
                                        className={cx(
                                            classNames.sectionHeader,
                                            classNames.stickyColumn
                                        )}
                                        style={{ position: 'sticky', left: 0 }}
                                    >
                                        {section.label}
                                        {section.meta !== undefined && <span>{section.meta}</span>}
                                    </th>
                                    {columns.map((c, i) => {
                                        const on = section.rows.filter((r) =>
                                            isChecked(r.key, c.key)
                                        ).length;
                                        const state: BoxState =
                                            on === 0
                                                ? 'off'
                                                : on === section.rows.length
                                                  ? 'on'
                                                  : 'partial';
                                        return (
                                            <td
                                                key={c.key}
                                                className={cx(
                                                    classNames.cell,
                                                    i < stickyColumns && classNames.stickyColumn
                                                )}
                                                style={cellStyle(c.key)}
                                            >
                                                {onToggleSection && (
                                                    <Box
                                                        state={state}
                                                        locked={c.locked}
                                                        classNames={classNames}
                                                        label={`${textOf(section.label)} · ${textOf(c.header)}`}
                                                        onToggle={() =>
                                                            onToggleSection(
                                                                section.key,
                                                                c.key,
                                                                state !== 'on'
                                                            )
                                                        }
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {section.rows.map((row) => (
                                    <tr key={row.key}>
                                        <th
                                            scope="row"
                                            className={cx(
                                                classNames.rowHeader,
                                                classNames.stickyColumn
                                            )}
                                            style={{ position: 'sticky', left: 0 }}
                                        >
                                            {row.header}
                                        </th>
                                        {columns.map((c, i) => {
                                            const on = isChecked(row.key, c.key);
                                            // EAA-1804: only real data cells can be "pending" — a
                                            // section row's Box is a rollup of many rows and has no
                                            // single edit to report as unsaved.
                                            const pending = isPending?.(row.key, c.key) ?? false;
                                            return (
                                                <td
                                                    key={c.key}
                                                    className={cx(
                                                        classNames.cell,
                                                        i < stickyColumns && classNames.stickyColumn
                                                    )}
                                                    style={cellStyle(c.key)}
                                                >
                                                    <Box
                                                        state={on ? 'on' : 'off'}
                                                        locked={c.locked || !onToggle}
                                                        pending={pending}
                                                        classNames={classNames}
                                                        label={`${textOf(row.header)} · ${textOf(c.header)}`}
                                                        onToggle={() =>
                                                            onToggle?.(row.key, c.key, !on)
                                                        }
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                        {sections.length === 0 && emptyMessage !== undefined && (
                            <tr>
                                <td colSpan={colCount} className={classNames.empty}>
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

type BoxState = 'on' | 'off' | 'partial';

interface BoxProps {
    state: BoxState;
    locked?: boolean;
    /** EAA-1804: edited locally, not yet saved — independent of `state`/`locked`. */
    pending?: boolean;
    label: string;
    classNames: MatrixTableClassNames;
    onToggle: () => void;
}

/**
 * A locked cell is a `<span>`, not a disabled `<button>`. A disabled control is
 * skipped by the keyboard and reads as "temporarily unavailable"; these columns
 * are permanently not-yours-to-edit, and their VALUE is still worth reading.
 */
function Box({
    state,
    locked,
    pending,
    label,
    classNames,
    onToggle,
}: BoxProps): React.ReactElement {
    // CVA resolves the 3 × 2 × 2 table and the app's override registry; the
    // `classNames` map is the caller's per-instance skin on top of it. Both,
    // not either — the recipe gives every matrix in the app the same hooks,
    // `classNames` lets one screen keep its handoff-exact CSS.
    const recipeCls = useRecipe(matrixBoxRecipe, {
        state,
        locked: locked ? 'yes' : 'no',
        // EAA-1804: default omitted callers to `'no'` explicitly — `pending` is
        // `undefined` for every existing caller, and the recipe's own
        // `defaultVariants` would cover that, but being explicit here keeps this
        // call symmetric with `locked` above rather than half-relying on it.
        pending: pending ? 'yes' : 'no',
    });
    const cls = cx(
        recipeCls,
        classNames.box,
        state !== 'off' && locked && classNames.boxLocked,
        state === 'on' && !locked && classNames.boxOn,
        state === 'partial' && !locked && classNames.boxPartial,
        pending && classNames.boxPending
    );

    // The mark ships with the component rather than coming from `classNames`:
    // a filled square with nothing in it is not a checkbox, and leaving the
    // glyph to the caller means the first caller who forgets renders a control
    // whose state cannot be read.
    const mark =
        state === 'off' ? null : (
            <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
            >
                {state === 'on' ? <path d="m5 12.5 4.5 4.5L19 7" /> : <path d="M6 12h12" />}
            </svg>
        );

    if (locked) {
        return (
            <span className={cls} role="img" aria-label={label} data-state={state}>
                {mark}
            </span>
        );
    }
    return (
        <button
            type="button"
            className={cls}
            aria-pressed={state === 'on'}
            aria-label={label}
            data-state={state}
            onClick={onToggle}
        >
            {mark}
        </button>
    );
}

/**
 * Best-effort plain text from a header node, for accessible names. Headers here
 * are a string or a string plus an icon, so this covers the real cases without
 * dragging in a renderer.
 */
function textOf(node: React.ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(textOf).join('');
    if (React.isValidElement(node)) {
        return textOf((node.props as { children?: React.ReactNode }).children);
    }
    return '';
}

export default MatrixTable;
